import { useCallback } from 'react';
import { useErrorContext, ErrorDetail } from './ErrorContext';

/**
 * Interface for the error tracker hook return value
 */
interface ErrorTracker {
  /**
   * Track an error with the error tracking system
   * @param {Error | string} error - The error object or error message
   * @param {object} options - Additional options for the error
   * @returns {string} The ID of the tracked error
   */
  trackError: (
    error: Error | string,
    options?: {
      component?: string;
      metadata?: Record<string, unknown>;
    }
  ) => string;

  /**
   * Track an error that occurs in a specific component
   * @param {Error | string} error - The error object or error message
   * @param {string} componentName - The name of the component where the error occurred
   * @param {object} metadata - Additional metadata about the error
   * @returns {string} The ID of the tracked error
   */
  trackComponentError: (
    error: Error | string,
    componentName: string,
    metadata?: Record<string, unknown>
  ) => string;

  /**
   * Check if a component has any tracked errors
   * @param {string} componentName - The name of the component to check
   * @returns {boolean} True if the component has errors
   */
  hasComponentErrors: (componentName: string) => boolean;

  /**
   * Get all errors for a specific component
   * @param {string} componentName - The name of the component
   * @returns {ErrorDetail[]} Array of errors for the component
   */
  getComponentErrors: (componentName: string) => ErrorDetail[];

  /**
   * Clear errors for a specific component
   * @param {string} componentName - The name of the component
   */
  clearComponentErrors: (componentName: string) => void;

  /**
   * Remove a specific error by ID
   * @param {string} errorId - The ID of the error to remove
   */
  removeError: (errorId: string) => void;

  /**
   * Clear all tracked errors
   */
  clearAllErrors: () => void;
}

/**
 * Hook to track and manage errors throughout the application
 * @returns {ErrorTracker} Functions to track and manage errors
 */
export const useErrorTracker = (): ErrorTracker => {
  const { addError, removeError, clearErrors, getErrorsForComponent } =
    useErrorContext();

  /**
   * Track an error with the error tracking system
   */
  const trackError = useCallback(
    (
      error: Error | string,
      options?: { component?: string; metadata?: Record<string, unknown> }
    ): string => {
      const errorMessage = error instanceof Error ? error.message : error;
      const errorStack = error instanceof Error ? error.stack : undefined;

      return addError({
        message: errorMessage,
        stack: errorStack,
        component: options?.component,
        metadata: options?.metadata,
      });
    },
    [addError]
  );

  /**
   * Track an error for a specific component
   */
  const trackComponentError = useCallback(
    (
      error: Error | string,
      componentName: string,
      metadata?: Record<string, unknown>
    ): string => {
      return trackError(error, { component: componentName, metadata });
    },
    [trackError]
  );

  /**
   * Check if a component has any errors
   */
  const hasComponentErrors = useCallback(
    (componentName: string): boolean => {
      return getErrorsForComponent(componentName).length > 0;
    },
    [getErrorsForComponent]
  );

  /**
   * Get all errors for a component
   */
  const getComponentErrors = useCallback(
    (componentName: string): ErrorDetail[] => {
      return getErrorsForComponent(componentName);
    },
    [getErrorsForComponent]
  );

  /**
   * Clear errors for a specific component
   */
  const clearComponentErrors = useCallback(
    (componentName: string): void => {
      const componentErrors = getErrorsForComponent(componentName);
      componentErrors.forEach((error) => removeError(error.id));
    },
    [getErrorsForComponent, removeError]
  );

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback((): void => {
    clearErrors();
  }, [clearErrors]);

  return {
    trackError,
    trackComponentError,
    hasComponentErrors,
    getComponentErrors,
    clearComponentErrors,
    removeError,
    clearAllErrors,
  };
};
